import { useState, useEffect, useRef } from 'react';
import { Icon, colorVar } from '../components/Icons';
import { AnimatedNumber } from '../components/AnimatedNumber';
import { useIsMobile } from '../hooks/useIsMobile';

/* ── ABOUT ── */
const DEFAULT_QUICK_FACTS = [
  {key:'Based', value:'Atlanta, Georgia'},
  {key:'Current Role', value:'Digital Producer · Corporate', sub:'TEGNA / Nexstar'},
  {key:'Path', value:'Local → Streaming → Corporate'},
  {key:'Target Role', value:'Director of Social · VP Brand', sub:'Head of Content'},
  {key:'Superpower', value:'Short Form Strategy'},
  {key:'Awards', value:'Murrow · 2× Emmy · NABJ'},
];
const DEFAULT_PITCH_CARDS = [
  {icon:'mic', color:'rose', title:"I know content from the inside.", desc:"Starting as a local digital producer, I moved into streaming and eventually corporate, so I understand how brand voice has to hold up across every format, platform, and audience."},
  {icon:'gear', color:'gold', title:"I build the machine.", desc:"SOPs, workflow systems, training programs, API automations. I don't just fix problems, I eliminate the conditions that create them. Repeatedly."},
  {icon:'chart', color:'sage', title:"The data backs it up.", desc:"Driving 12.7M views a month, every month, across 60+ stations. #1 on my team in every Instagram and TikTok metric. These aren't estimates. They're the export."},
];

export function About({ content }) {
  const isMobile = useIsMobile();
  const p = content?.profile || {};
  const quickFacts = (p.quickFacts && p.quickFacts.length) ? p.quickFacts : DEFAULT_QUICK_FACTS;
  const pitchCards = (p.pitchCards && p.pitchCards.length) ? p.pitchCards : DEFAULT_PITCH_CARDS;

  return (
    <div className="page-enter">
      <PageHeader title="About" accent="Me" sub="// Built in media. Sharpened in strategy. Ready to lead." />
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.15fr 1fr', gap: 14, marginBottom: 14 }}>
        <HeroCard profile={p} />
        <div className="panel">
          <div className="panel-header"><span className="panel-title">The Quick Facts</span></div>
          <div className="panel-body">
            {quickFacts.map((f, i) => (
              <div key={f.key} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'11px 0',borderBottom:i<quickFacts.length-1?'1px solid var(--border)':'none'}}>
                <span style={{fontSize:12,fontWeight:700,color:'var(--text3)',fontFamily:"'Courier Prime',monospace",letterSpacing:'.06em',textTransform:'uppercase'}}>{f.key}</span>
                <div style={{textAlign:'right'}}>
                  <div style={{fontSize:14,fontWeight:700,color:'var(--ink)'}}>{f.value}</div>
                  {f.sub && <div style={{fontSize:11,color:'var(--deep-rose)'}}>{f.sub}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="panel">
        <div className="panel-header"><span className="panel-title">The Pitch</span><span className="chip chip-ink">In My Own Words</span></div>
        <div className="panel-body" style={{display:'grid',gridTemplateColumns:isMobile ? '1fr' : '1fr 1fr 1fr',gap:20}}>
          {pitchCards.map(({icon,color,title,desc}) => (
            <div key={title}>
              <div style={{width:44,height:44,borderRadius:14,background:colorVar(color,'gradient'),display:'flex',alignItems:'center',justifyContent:'center',marginBottom:10}}>
                <Icon name={icon} size={22} stroke="white" strokeWidth={1.8}/>
              </div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,fontWeight:700,fontStyle:'italic',color:'var(--ink)',marginBottom:6}}>{title}</div>
              <div style={{fontSize:14,color:'var(--text2)',lineHeight:1.65}}>{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── METRICS ── */
export function Metrics({ content }) {
  const isMobile = useIsMobile();
  const metrics = content?.metrics || [];
  return (
    <div className="page-enter">
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:8}}>
        <div><PageHeader title="Live" accent="Metrics" sub="// 163 active posting days · Aug 2025–Aug 2026 · 60+ stations" /></div>
        <span className="chip chip-sage" style={{fontSize:12,padding:'5px 12px',flexShrink:0}}>13,522 Posts Analyzed</span>
      </div>
      <div style={{display:'grid',gridTemplateColumns:isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)',gap:10,marginBottom:14}}>
        {metrics.map(m => (
          <div key={m.id} className="panel" style={{padding:16,position:'relative',overflow:'hidden'}}>
            <div style={{position:'absolute',top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${colorVar(m.color,'deep')},transparent)`}}/>
            <div style={{fontFamily:"'Courier Prime',monospace",fontSize:11,letterSpacing:'.1em',textTransform:'uppercase',color:'var(--text3)',marginBottom:6}}>{m.label}</div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:26,fontWeight:900,lineHeight:1,marginBottom:2,color:colorVar(m.color,'deep')}}><AnimatedNumber value={m.value} /></div>
            <div style={{fontSize:12,color:'var(--text3)',fontWeight:600}}>{m.sub}</div>
          </div>
        ))}
      </div>
      {/* Ranking row */}
      <div style={{display:'grid',gridTemplateColumns:isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)',gap:10,marginBottom:14}}>
        {[
          {color:'rose',label:'Views / Month',value:'12.7M',sub:'12-month average'},
          {color:'gold',label:'Views / Week',value:'2.9M',sub:'sustained, not a spike'},
          {color:'sage',label:'Views / Active Day',value:'933K',sub:'across 163 posting days'},
          {color:'periwinkle',label:'Of Team Output',value:'55%',sub:'highest volume of 4'},
        ].map((r,i) => (
          <div key={i} style={{background:`linear-gradient(135deg,${colorVar(r.color,'bg')},${colorVar(r.color === 'rose' ? 'periwinkle' : r.color,'bg')})`,borderRadius:14,padding:16,border:'1.5px solid var(--border2)'}}>
            <div style={{fontFamily:"'Courier Prime',monospace",fontSize:11,letterSpacing:'.1em',textTransform:'uppercase',color:colorVar(r.color,'deep'),marginBottom:6}}>{r.label}</div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:900,color:colorVar(r.color,'deep'),lineHeight:1}}><AnimatedNumber value={r.value} /></div>
            <div style={{fontSize:12,color:'var(--text2)',marginTop:3,fontWeight:700}}>{r.sub}</div>
          </div>
        ))}
      </div>
      <div style={{display:'grid',gridTemplateColumns:isMobile ? '1fr' : '1.5fr 1fr',gap:14}}>
        <div className="panel">
          <div className="panel-header"><span className="panel-title">Platform Breakdown</span><span className="chip chip-rose">Aug 2025–Aug 2026</span></div>
          <div className="panel-body" style={{display:'flex',flexDirection:'column',gap:16}}>
            {[{color:'rose',name:'Facebook',val:'93.7M views',pct:100,meta:'4,182 posts · 65.4M reel plays · 9.5M engagements'},{color:'periwinkle',name:'Instagram',val:'31.9M views',pct:34,meta:'3,868 posts · 30M reel plays · 23.9M reach'},{color:'gold',name:'TikTok',val:'22.3M views',pct:24,meta:'1,954 posts · 1M likes · 49% of team total'}].map(p => (
              <div key={p.name}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:5}}>
                  <span style={{fontSize:14,fontWeight:800,color:colorVar(p.color,'deep')}}>{p.name}</span>
                  <span style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:900,color:colorVar(p.color,'deep')}}>{p.val}</span>
                </div>
                <div style={{height:10,borderRadius:6,background:colorVar(p.color,'bg'),overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${p.pct}%`,borderRadius:6,background:colorVar(p.color,'deep')}}/>
                </div>
                <div style={{fontSize:12,color:'var(--text3)',marginTop:4}}>{p.meta}</div>
              </div>
            ))}
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:5}}>
                <span style={{fontSize:13,fontWeight:800,color:'var(--deep-sage)'}}>YouTube retention</span>
                <span style={{fontFamily:"'Playfair Display',serif",fontSize:18,fontWeight:900,color:'var(--deep-sage)'}}>64.1%</span>
              </div>
              <div style={{position:'relative',height:10,borderRadius:6,background:'var(--mint)',overflow:'hidden'}}>
                <div style={{height:'100%',width:'64.1%',borderRadius:6,background:'var(--deep-sage)'}}/>
              </div>
              <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>avg across 3,071 videos · 1.73M minutes watched</div>
            </div>
          </div>
        </div>
        <div className="panel">
          <div className="panel-header"><span className="panel-title">Why It Matters</span></div>
          <div className="panel-body" style={{display:'flex',flexDirection:'column',gap:10}}>
            {[{n:'#1',l:'on my team in every Instagram and TikTok metric',c:'var(--crimson)'},{n:'55%',l:"of my team's total output, as one of four producers",c:'var(--deep-sage)'},{n:'104M',l:'views in my most recent 90 days, a 34.7M monthly pace',c:'var(--deep-periwinkle)'}].map(c => (
              <div key={c.n} style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',background:'var(--warm)',borderRadius:10,border:'1px solid var(--border)'}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:24,fontWeight:900,color:c.c,minWidth:72,lineHeight:1}}><AnimatedNumber value={c.n} /></div>
                <div style={{fontSize:13,color:'var(--text2)',lineHeight:1.4}}>{c.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── SKILLS ── */
export function Skills({ content }) {
  const isMobile = useIsMobile();
  const skills = content?.skills || [];
  const proficiency = content?.proficiency || [];
  const [lit, setLit] = useState({});

  return (
    <div className="page-enter">
      <PageHeader title="Skills &" accent="Expertise" sub="// The tools. The strategy. The proof." />
      <div style={{display:'grid',gridTemplateColumns:isMobile ? '1fr' : '1fr 1fr',gap:12,marginBottom:14}}>
        {skills.map(s => (
          <div key={s.id} className="panel" style={{padding:20,position:'relative',overflow:'hidden',cursor:'default',transition:'all .22s'}}
            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-4px)';e.currentTarget.style.boxShadow='var(--shadow-hover)';}}
            onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='var(--shadow)';}}>
            <div style={{position:'absolute',bottom:0,left:0,right:0,height:3,background:colorVar(s.color,'gradient')}}/>
            <div style={{width:44,height:44,borderRadius:14,background:colorVar(s.color,'gradient'),display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12}}>
              <Icon name={s.icon || 'bulb'} size={24} stroke="white" strokeWidth={1.8}/>
            </div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,fontWeight:700,fontStyle:'italic',color:'var(--ink)',marginBottom:6}}>{s.name}</div>
            <div style={{fontSize:14,color:'var(--text2)',lineHeight:1.6,marginBottom:12}}>{s.desc}</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
              {(s.tags || []).map(t => <span key={t} style={{fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:12,background:colorVar(s.color,'bg'),color:colorVar(s.color,'text'),border:`1px solid ${colorVar(s.color,'border')}`}}>{t}</span>)}
            </div>
          </div>
        ))}
      </div>
      <div className="panel">
        <div className="panel-header">
          <span className="panel-title">Proficiency</span>
          <span className="chip chip-sage">Tool-verified</span>
        </div>
        <div style={{display:'grid',gridTemplateColumns:isMobile ? '1fr' : '1fr 1fr',gap:1,background:'var(--border)',borderRadius:'0 0 12px 12px',overflow:'hidden'}}>
          {proficiency.map(s => (
            <ProficiencyBar
              key={s.id}
              s={s}
              animated={!!lit[s.id]}
              onVisible={() => setLit(p => ({...p, [s.id]: true}))}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ProficiencyBar({ s, animated, onVisible }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { onVisible(); obs.disconnect(); }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const level = s.pct >= 92 ? 'Expert' : s.pct >= 80 ? 'Advanced' : s.pct >= 70 ? 'Proficient' : 'Building';
  const lc = s.pct >= 92 ? 'var(--crimson)' : s.pct >= 80 ? 'var(--deep-rose)' : 'var(--deep-sage)';
  const lb = s.pct >= 92 ? 'rgba(196,104,122,.1)' : s.pct >= 80 ? 'rgba(180,80,100,.08)' : 'rgba(74,145,120,.1)';

  return (
    <div ref={ref} style={{background:'white',padding:'14px 18px',transition:'background .15s'}}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--warm)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
        <span style={{fontSize:14,fontWeight:800,color:'var(--ink)'}}>{s.name}</span>
        <span style={{fontSize:10,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',color:lc,background:lb,padding:'2px 8px',borderRadius:10}}>{level}</span>
      </div>
      <div style={{height:5,background:'var(--blush)',borderRadius:3,marginBottom:6,overflow:'hidden'}}>
        <div style={{height:'100%',width:animated?`${s.pct}%`:'0%',background:s.color,borderRadius:3,transition:'width 1.4s cubic-bezier(.4,0,.2,1)'}}/>
      </div>
      <div style={{fontSize:12,color:'var(--text3)',fontFamily:"'Courier Prime',monospace",letterSpacing:'.03em'}}>{s.sub}</div>
    </div>
  );
}

/* ── SYSTEMS ── */
export function Systems({ content }) {
  const isMobile = useIsMobile();
  const systems = content?.systems || [];
  return (
    <div className="page-enter">
      <PageHeader title="Systems" accent="Built" sub="// Real work · measurable results · no fluff" />
      <div style={{display:'grid',gridTemplateColumns:isMobile ? '1fr' : '1fr 1fr',gap:12}}>
        {systems.map(s => (
          <div key={s.id} className="panel" style={{borderRadius:14,cursor:'pointer',position:'relative',transition:'all .2s'}}
            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow='var(--shadow-hover)';}}
            onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='var(--shadow)';}}>
            <div style={{position:'absolute',top:0,left:0,bottom:0,width:3,background:colorVar(s.color,'deep')}}/>
            <div style={{padding:'16px 16px 16px 20px'}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8,marginBottom:7,flexWrap:'wrap'}}>
                <span style={{fontSize:14,fontWeight:800,color:'var(--ink)',flex:1,minWidth:0}}>{s.title}</span>
                <span className={`chip chip-${s.color === 'ink' ? 'ink' : s.color}`} style={{flexShrink:0}}>{s.tag}</span>
              </div>
              <div style={{display:'flex',gap:18,marginBottom:10,flexWrap:'wrap'}}>
                {(s.metrics||[]).map((m,i) => (
                  <div key={i}>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontWeight:900,color:colorVar(s.color,'deep'),lineHeight:1}}><AnimatedNumber value={m.value} /></div>
                    <div style={{fontSize:10,color:'var(--text3)',marginTop:3,fontFamily:"'Courier Prime',monospace",letterSpacing:'.04em'}}>{m.sub}</div>
                  </div>
                ))}
              </div>
              <div style={{fontSize:13,color:'var(--text2)',lineHeight:1.6}}>{s.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── AWARDS ── */
const AWARD_CHIP_COLORS = ['rose','sage','gold','periwinkle','ink','rose','sage','gold'];
const DEFAULT_ALSO_KNOWN_FOR = ['Staff Trainer','Onboarding Lead','Mentorship','Conference Speaker','Journalism Educator','High School Mentor','Team Builder','Process Creator'];

export function Awards({ content }) {
  const isMobile = useIsMobile();
  const awards = content?.awards || [];
  const alsoKnownFor = (content?.profile?.alsoKnownFor && content.profile.alsoKnownFor.length)
    ? content.profile.alsoKnownFor
    : DEFAULT_ALSO_KNOWN_FOR;
  return (
    <div className="page-enter">
      <PageHeader title="Recognition &" accent="Awards" sub="// The receipts" />
      <div style={{display:'grid',gridTemplateColumns:isMobile ? '1fr' : '1.15fr 1fr',gap:14}}>
        <div className="panel">
          <div className="panel-header"><span className="panel-title">The Full List</span><span className="chip chip-gold">Industry Recognized</span></div>
          <div style={{padding:'4px 18px 10px'}}>
            {awards.map(a => (
              <div key={a.id} style={{display:'flex',gap:12,alignItems:'center',padding:'12px 0',borderBottom:'1.5px solid var(--border)'}}>
                <div style={{width:40,height:40,borderRadius:12,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,background:'linear-gradient(135deg,var(--butter),var(--gold))'}}>
                  <Icon name={a.icon || 'trophy'} size={20} stroke="var(--deep-gold)" strokeWidth={1.8}/>
                </div>
                <div>
                  <div style={{fontSize:15,fontWeight:800,color:'var(--ink)'}}>{a.name}</div>
                  <div style={{fontSize:13,color:'var(--text3)',marginTop:1}}>{a.org}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <div className="panel">
            <div className="panel-header"><span className="panel-title">By the Numbers</span></div>
            <div className="panel-body" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              {[{color:'rose',val:'6',label:'Industry Awards'},{color:'gold',val:'2×',label:'Emmy Nominated'},{color:'sage',val:'#1',label:'IG & TikTok, My Team'},{color:'periwinkle',val:'8+',label:'Years in Media'}].map(n => (
                <div key={n.label} style={{textAlign:'center',padding:14,background:colorVar(n.color,'bg'),borderRadius:12}}>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:900,color:colorVar(n.color,'deep')}}><AnimatedNumber value={n.val} /></div>
                  <div style={{fontSize:12,fontWeight:700,color:'var(--text2)',marginTop:2}}>{n.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="panel">
            <div className="panel-header"><span className="panel-title">Also Known For</span></div>
            <div className="panel-body" style={{display:'flex',flexWrap:'wrap',gap:7}}>
              {alsoKnownFor.map((t, i) => (
                <span key={t} className={`chip chip-${AWARD_CHIP_COLORS[i % AWARD_CHIP_COLORS.length]}`}>{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── TIMELINE ── */
const ctaLinkStyle = {
  display:'flex', alignItems:'center', gap:8, width:'100%',
  padding:'8px 12px', borderRadius:8, border:'1.5px solid var(--border2)',
  background:'white', color:'var(--text)', fontFamily:"'Nunito',sans-serif",
  fontSize:13, fontWeight:700, textDecoration:'none', transition:'all .15s', cursor:'pointer',
};
const ctaHoverOn = e => { e.currentTarget.style.borderColor='var(--deep-rose)'; e.currentTarget.style.color='var(--crimson)'; e.currentTarget.style.background='var(--blush)'; };
const ctaHoverOff = e => { e.currentTarget.style.borderColor='var(--border2)'; e.currentTarget.style.color='var(--text)'; e.currentTarget.style.background='white'; };

const DEFAULT_WHAT_NEXT_TEXT = "Ready for a seat at the table where strategy meets execution. I've spent 8+ years building the machine. Now I want to run it. Senior brand and social leadership, at scale.";

export function Timeline({ content }) {
  const isMobile = useIsMobile();
  const tl = content?.timeline || [];
  const roles = content?.targetRoles || [];
  const p = content?.profile || {};
  const whatNextText = p.whatNextText || DEFAULT_WHAT_NEXT_TEXT;

  const ctaLinks = [
    {icon:'email', label:'Send a Message', onClick:() => window.dispatchEvent(new CustomEvent('mr:openContact'))},
    {icon:'linkedin', label:'LinkedIn Profile', href:p.linkedin||'#'},
    {icon:'resume', label:'Resume', href:p.resumeUrl||'#resume'},
  ];

  return (
    <div className="page-enter">
      <PageHeader title="Career" accent="Timeline" sub="// Local producer, streaming, corporate" />
      <div style={{display:'grid',gridTemplateColumns:isMobile ? '1fr' : '1.15fr 1fr',gap:14}}>
        <div className="panel">
          <div className="panel-header"><span className="panel-title">The Journey</span><span className="chip chip-periwinkle">Local to Corporate</span></div>
          <div style={{padding:'4px 18px 10px'}}>
            {tl.map((t,i) => (
              <div key={t.id} style={{display:'flex',gap:14,padding:'13px 0',borderBottom:i<tl.length-1?'1.5px solid var(--border)':'none',alignItems:'flex-start'}}>
                <div style={{minWidth:46,display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                  <div style={{fontFamily:"'Courier Prime',monospace",fontSize:11,color:'var(--text3)'}}>{t.year}</div>
                  <div style={{width:10,height:10,borderRadius:'50%',background:t.color||'var(--rose)',flexShrink:0}}/>
                </div>
                <div>
                  <div style={{fontSize:15,fontWeight:800,color:'var(--ink)',marginBottom:3}}>{t.title}</div>
                  <div style={{fontSize:13,color:'var(--text2)',lineHeight:1.5}}>{t.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <div className="panel">
            <div className="panel-header"><span className="panel-title">What's Next</span></div>
            <div className="panel-body">
              <p style={{fontSize:15,color:'var(--text2)',lineHeight:1.7,marginBottom:14}}>{whatNextText}</p>
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {roles.map(r => (
                  <div key={r.id} style={{padding:'10px 13px',background:colorVar(r.color,'bg'),borderRadius:10,fontSize:14,fontWeight:800,color:colorVar(r.color,'deep')}}>{r.title}</div>
                ))}
              </div>
            </div>
          </div>
          <div className="panel">
            <div className="panel-header"><span className="panel-title">Let's Talk</span></div>
            <div className="panel-body" style={{display:'flex',flexDirection:'column',gap:8}}>
              {ctaLinks.map(l => l.onClick
                ? <button key={l.icon} onClick={l.onClick} style={ctaLinkStyle} onMouseEnter={ctaHoverOn} onMouseLeave={ctaHoverOff}><Icon name={l.icon} size={14} stroke="var(--deep-rose)"/>{l.label}</button>
                : <a key={l.icon} href={l.href} style={ctaLinkStyle} onMouseEnter={ctaHoverOn} onMouseLeave={ctaHoverOff}><Icon name={l.icon} size={14} stroke="var(--deep-rose)"/>{l.label}</a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── SHARED ── */
function PageHeader({ title, accent, sub }) {
  return (
    <div style={{marginBottom:20}}>
      <div style={{fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:900,fontStyle:'italic',color:'var(--ink)',marginBottom:4}}>
        {title} <span style={{color:'var(--deep-rose)'}}>{accent}</span>
      </div>
      <div style={{fontFamily:"'Courier Prime',monospace",fontSize:12,color:'var(--text3)',letterSpacing:'.04em'}}>{sub}</div>
    </div>
  );
}

function HeroCard({ profile }) {
  const isMobile = useIsMobile();
  const aboutPhotos = (Array.isArray(profile.aboutPhotos) && profile.aboutPhotos.length)
    ? profile.aboutPhotos
    : (profile.photo ? [profile.photo] : []);
  const aboutPhotoMeta = profile.aboutPhotoMeta || [];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (aboutPhotos.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % aboutPhotos.length), 3500);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aboutPhotos.length]);

  return (
    <div style={{background:'linear-gradient(135deg,var(--ink) 0%,var(--plum) 55%,#3d2247 100%)',borderRadius:18,color:'white',position:'relative',overflow:'hidden',boxShadow:'var(--shadow-hover)',display:'flex',flexDirection:isMobile?'column':'row',gap:4}}>
      <div style={{position:'absolute',top:-50,right:-50,width:200,height:200,borderRadius:'50%',background:'rgba(247,207,214,.07)'}}/>
      {/* Photo column */}
      <div style={{width:isMobile?'100%':240,minWidth:isMobile?'auto':240,padding:isMobile?'16px 16px 0':14,position:'relative',zIndex:1,display:'flex',flexDirection:'column',alignItems:'stretch',gap:8}}>
        <div style={{position:'relative',width:'100%',height:isMobile?240:'100%',minHeight:isMobile?240:280,borderRadius:14,overflow:'hidden',boxShadow:'0 8px 30px rgba(0,0,0,.25)'}}>
          {aboutPhotos.length > 0
            ? aboutPhotos.map((src, i) => {
                const meta = aboutPhotoMeta[i] || {};
                const zoom = meta.zoom ?? 1;
                const x = meta.x ?? 50;
                const y = meta.y ?? 0;
                return (
                  <img key={i} src={src} alt={profile.name}
                    style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',objectPosition:`${x}% ${y}%`,transform:`scale(${zoom})`,transformOrigin:`${x}% ${y}%`,opacity:i===idx?1:0,transition:'opacity .8s ease'}}/>
                );
              })
            : <div style={{display:'flex',alignItems:'center',justifyContent:'center',width:'100%',height:'100%',background:'rgba(255,255,255,.05)',border:'2px dashed rgba(255,255,255,.18)'}}><Icon name="camera" size={40} stroke="rgba(255,255,255,.35)" strokeWidth={1.5}/></div>
          }
          {aboutPhotos.length > 1 && (
            <div style={{position:'absolute',bottom:10,left:0,right:0,display:'flex',gap:6,justifyContent:'center',zIndex:2}}>
              {aboutPhotos.map((_, i) => (
                <span key={i} style={{width:6,height:6,borderRadius:'50%',background:i===idx?'white':'rgba(255,255,255,.45)',transition:'background .3s'}}/>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Bio column */}
      <div style={{flex:1,padding:isMobile?'16px 22px 22px':'22px 22px 22px 8px',position:'relative',zIndex:1}}>
        <div style={{fontFamily:"'Courier Prime',monospace",fontSize:11,letterSpacing:'.14em',textTransform:'uppercase',color:'var(--rose)',marginBottom:9}}>// 12.7M views a month · Murrow Award · 2× Emmy</div>
        <div style={{fontFamily:"'Playfair Display',serif",fontSize:isMobile?20:22,fontWeight:900,fontStyle:'italic',lineHeight:1.15,marginBottom:8}}>
          The operator<br/>behind the <span style={{color:'var(--rose)'}}>operation.</span>
        </div>
        <p style={{fontSize:14,color:'rgba(255,255,255,.88)',lineHeight:1.7,marginBottom:10}}>{profile.aboutBio}</p>
        <p style={{fontSize:13,color:'rgba(255,255,255,.72)',lineHeight:1.5,fontStyle:'italic'}}>{profile.pitch}</p>
      </div>
    </div>
  );
}
